import React, { useState, useEffect } from "react";
import Layout from "../pages/Layout";
import Advertise from "../pages/Advertise";

const LivestockFeedCost = () => {
  // State variables
  const [totalFeedCosts, setTotalFeedCosts] = useState(0); // Total feed costs
  const [feedCostUnit, setFeedCostUnit] = useState("USD"); // Unit for feed cost
  const [totalLivestock, setTotalLivestock] = useState(0); // Total number of livestock
  const [feedCostPerUnit, setFeedCostPerUnit] = useState(0); // Calculated cost per unit of livestock

  // Unit conversion factors for feed cost and livestock count
  const unitConversion = {
    // Feed Cost Units
    USD: 1,
    EUR: 1.1,  // Conversion factor from USD to EUR
    GBP: 1.3,  // Conversion factor from USD to GBP
    INR: 75,   // Conversion factor from USD to INR
    AUD: 1.5,  // Conversion factor from USD to AUD
    CAD: 1.25, // Conversion factor from USD to CAD
    JPY: 110,  // Conversion factor from USD to JPY
    CNY: 7,    // Conversion factor from USD to CNY
    BRL: 5.4,  // Conversion factor from USD to BRL
    ZAR: 18,   // Conversion factor from USD to ZAR

    // Livestock Units (assumed as animals)
    head: 1,    // In terms of animals per unit
    cows: 1,    // Same unit as head for simplicity
    pigs: 1,    // Same unit as head for simplicity
    chickens: 1, // Same unit as head for simplicity
    sheep: 1,   // Same unit as head for simplicity
    goats: 1,   // Same unit as head for simplicity
    horses: 1,  // Same unit as head for simplicity
    rabbits: 1, // Same unit as head for simplicity
    ducks: 1,   // Same unit as head for simplicity
    buffalo: 1, // Same unit as head for simplicity
  };

  // Event handlers for input changes
  const handleTotalFeedCostsChange = (e) =>
    setTotalFeedCosts(parseFloat(e.target.value) || 0);
  const handleFeedCostUnitChange = (e) => setFeedCostUnit(e.target.value);
  const handleTotalLivestockChange = (e) =>
    setTotalLivestock(parseFloat(e.target.value) || 0);

  // Calculate Feed Cost per unit of livestock
  useEffect(() => {
    if (totalLivestock > 0) {
      const normalizedFeedCosts = totalFeedCosts * unitConversion[feedCostUnit];
      const costPerUnit = normalizedFeedCosts / totalLivestock;
      setFeedCostPerUnit(costPerUnit);
    }

    // This will initialize or reinitialize all popovers on the page whenever the component renders
    const popoverTriggerList = [].slice.call(
      document.querySelectorAll('[data-bs-toggle="popover"]')
    );

    // Clean up any existing popover instances
    popoverTriggerList.forEach((popoverTriggerEl) => {
      const popover = window.bootstrap.Popover.getInstance(popoverTriggerEl); // Check if instance already exists
      if (popover) {
        popover.dispose(); // Dispose of existing popover
      }
    });

    // Re-initialize popovers
    popoverTriggerList.forEach((popoverTriggerEl) => {
      new window.bootstrap.Popover(popoverTriggerEl); // Re-initialize popovers
    });


  }, [totalFeedCosts, feedCostUnit, totalLivestock]);

  return (
    <Layout title="Livestock Feed Cost Calculator" path="livestock-feed-cost">
      <section className="section-padding" id="livestock-feed-cost">
        <div className="container mt-5">
          <div className="row">
            {/* Calculator Section */}
            <div className="col-lg-9 col-md-8 mb-4">
              <div
                className="p-4 border rounded shadow"
                style={{ border: "1px solid black" }}
              >
                <h3 className="text-center mb-4">Livestock Feed Cost Calculator</h3>
                <div className="container">
                  <div className="row gy-3">
                    {/* Input for Total Feed Costs */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="totalFeedCosts" className="form-label">
                        Total Feed Costs
                        <span
                    className="ms-2 text-muted"
                    data-bs-toggle="popover"
                    data-bs-html="true"
                    data-bs-placement="top"
                    data-bs-content="<strong>Total Feed Costs:</strong> The total amount spent on feed for all livestock. This helps determine the overall expenditure for maintaining the livestock."
                    style={{ cursor: "pointer" }}
                  >
                    <i className="bi bi-info-circle"></i>
                  </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="totalFeedCosts"
                        placeholder="Enter total feed costs"
                        value={totalFeedCosts || ""}
                        onChange={handleTotalFeedCostsChange}
                      />
                      <select
                        className="form-select mt-2"
                        value={feedCostUnit}
                        onChange={handleFeedCostUnitChange}
                      >
                        <option value="USD">USD</option>
                        <option value="EUR">EUR</option>
                        <option value="GBP">GBP</option>
                        <option value="INR">INR</option>
                        <option value="AUD">AUD</option>
                        <option value="CAD">CAD</option>
                        <option value="JPY">JPY</option>
                        <option value="CNY">CNY</option>
                        <option value="BRL">BRL</option>
                        <option value="ZAR">ZAR</option>
                      </select>
                    </div>

                    {/* Input for Total Livestock */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="totalLivestock" className="form-label">
                        Total Livestock
                        <span
                    className="ms-2 text-muted"
                    data-bs-toggle="popover"
                    data-bs-html="true"
                    data-bs-placement="top"
                    data-bs-content="<strong>Total Livestock:</strong> The total number of livestock in your care. This includes all animals, such as cows, pigs, chickens, sheep, and others."
                    style={{ cursor: "pointer" }}
                  >
                    <i className="bi bi-info-circle"></i>
                  </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="totalLivestock"
                        placeholder="Enter total livestock"
                        value={totalLivestock || ""}
                        onChange={handleTotalLivestockChange}
                      />
                      <select className="form-select mt-2">
                        <option value="head">Head</option>
                        <option value="cows">Cows</option>
                        <option value="pigs">Pigs</option>
                        <option value="chickens">Chickens</option>
                        <option value="sheep">Sheep</option>
                        <option value="goats">Goats</option>
                        <option value="horses">Horses</option>
                        <option value="rabbits">Rabbits</option>
                        <option value="ducks">Ducks</option>
                        <option value="buffalo">Buffalo</option>
                      </select>
                    </div>
                  </div>

                  {/* Result Section */}
                  <div className="mt-4">
                    <div className="p-3 text-center">
                      <h6>
                        Feed Cost Per Unit of Livestock:{" "}
                        <span className="fw-bold ms-3">
                          {feedCostPerUnit === 0
                            ? "0"
                            : feedCostPerUnit.toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}{" "}
                          {feedCostUnit} per{" "}
                          {totalLivestock > 1 ? "head" : "unit"}
                        </span>
                      </h6>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Advertisement Section */}
            <Advertise />
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="introduction-section" id="introduction">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="p-5">
                <h3 className="text-center mb-5">
                  Introduction to Livestock Feed Cost Calculation
                </h3>

                {/* What is Feed Cost per Livestock? */}
                <h4 className="mt-3 mb-3">What is Feed Cost per Livestock?</h4>
                <p>
                  **Feed Cost per Livestock** refers to the total cost of feeding the livestock divided by the number of livestock. This helps farmers and agribusinesses assess their expenses and manage their resources more effectively.
                </p>

                {/* Why Use the Livestock Feed Cost Calculator? */}
                <h4 className="mt-4 mb-3">Why Use the Livestock Feed Cost Calculator?</h4>
                <p>
                  This calculator helps farmers determine how much they are spending per animal, enabling them to optimize feed purchases, manage costs, and improve profit margins.
                </p>

                {/* Benefits of Using the Livestock Feed Cost Calculator */}
                <h4 className="mt-4 mb-3">Benefits of Using the Livestock Feed Cost Calculator</h4>
                <ul>
                  <li>
                    <strong>Optimizes Feed Management:</strong> Helps in selecting the right feed and managing feed costs efficiently.
                  </li>
                  <li>
                    <strong>Cost Control:</strong> Enables better budgeting and control of livestock feeding expenses.
                  </li>
                  <li>
                    <strong>Improves Profitability:</strong> Identifying cost-effective feeding strategies can improve overall farm profitability.
                  </li>
                </ul>

                {/* Suitable For */}
                <h4 className="mt-4 mb-3">Who Should Use the Livestock Feed Cost Calculator?</h4>
                <p>
                  This calculator is suitable for:
                </p>
                <ul>
                  <li>
                    <strong>Livestock Farmers:</strong> Track feeding expenses per unit of livestock.
                  </li>
                  <li>
                    <strong>Animal Husbandry:</strong> Helps optimize feeding strategies and ensure healthy livestock growth.
                  </li>
                  <li>
                    <strong>Agribusinesses:</strong> For cost analysis and resource management in livestock-related ventures.
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default LivestockFeedCost;
